import { useState, useMemo } from "react";
import AssetReportMap from "./AssetReportMap";
import { useSelector } from "react-redux";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapMinus } from "lucide-react";

const AssetReports = () => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [divisionFilter, setDivisionFilter] = useState("All");
  const [subDivisionFilter, setSubDivisionFilter] = useState("All");

  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";
  const borderThem = themeMode ? "border_them_2" : "border-primary";

  const statusColors = {
    Active: "#28a745",
    Inactive: "#dc3545",
    Maintenance: "#ffc107",
    All: "#6c757d",
    Default: "#6c757d",
  };

  const JAMMU_COORDINATES = [
    { lat: 32.733, lng: 74.8648 },
    { lat: 32.706, lng: 74.8661 }, 
    { lat: 32.6934, lng: 74.8791 }, 
    { lat: 32.7533, lng: 74.8402 },
    { lat: 32.6888, lng: 74.8945 }, 
    { lat: 32.7351, lng: 74.8504 }, 
    { lat: 32.768, lng: 74.912 }, 
    { lat: 32.718, lng: 74.825 }, 
    { lat: 32.645, lng: 74.921 }, 
    { lat: 32.742, lng: 74.805 }, 
    { lat: 32.701, lng: 74.835 }, 
    { lat: 32.748, lng: 74.875 }, 
    { lat: 32.675, lng: 74.905 }, 
    { lat: 32.725, lng: 74.852 }, 
    { lat: 32.76, lng: 74.83 },
  ];

  const DUMMY_ASSETS = useMemo(() => {
    const divisions = ["Jammu West", "Jammu East", "Samba South"];
    const subDivs = ["Sub-Div 01", "Sub-Div 02", "Sub-Div 03"];

    return Array.from({ length: 25 }).map((_, i) => {
      const coord = JAMMU_COORDINATES[i % JAMMU_COORDINATES.length];

      return {
        id: i + 1,
        name: `Jammu-Asset-${i + 1}`,
        location: "Jammu District",
        status:
          i % 3 === 0 ? "Active" : i % 3 === 1 ? "Inactive" : "Maintenance",
        division: divisions[i % divisions.length],
        subDivision: subDivs[i % subDivs.length],
        lat: coord.lat,
        lng: coord.lng,
      };
    });
  }, []);

  return (
    <div className={`w-full h-full ${bgColorClass}`}>
      <div
        className={`py-4 flex flex-wrap justify-between items-center min-h-[57px] md:px-12 px-6 border-b ${borderThem} gap-4`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center gap-x-2`}
        >
          <MapMinus size={16} />
          <p className="font-semibold">Jammu Asset Location Report</p>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          {/* Status Filter */}
          <Select onValueChange={setStatusFilter} defaultValue="All">
            <SelectTrigger
              className={`w-[140px] h-8 text-[11px] bg-transparent focus:ring-0 ${textColorClass} ${borderThem}`}
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className={`${bgColorClass} border ${borderThem}`}>
              <SelectItem value="All">
                <div className={`flex items-center ${textColorClass}`}>
                  WTP
                </div>
              </SelectItem>
              <SelectItem value="Active">
                <span className={textColorClass}>Tubewell</span>
              </SelectItem>
              <SelectItem value="Inactive">
                <span className={textColorClass}>Pumping Stations</span>
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Division Filter */}
          <Select onValueChange={setDivisionFilter} defaultValue="All">
            <SelectTrigger
              className={`w-[140px] h-8 text-[11px] bg-transparent focus:ring-0 ${textColorClass} ${borderThem}`}
            >
              <SelectValue placeholder="Division" />
            </SelectTrigger>
            <SelectContent className={`${bgColorClass} border ${borderThem}`}>
              <SelectItem value="All">
                <span className={textColorClass}>All Divisions</span>
              </SelectItem>
              <SelectItem value="Jammu West">
                <span className={textColorClass}>Jammu West</span>
              </SelectItem>
              <SelectItem value="Jammu East">
                <span className={textColorClass}>Jammu East</span>
              </SelectItem>
              <SelectItem value="Samba South">
                <span className={textColorClass}>Samba South</span>
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Sub Division Filter */}
          <Select onValueChange={setSubDivisionFilter} defaultValue="All">
            <SelectTrigger
              className={`w-[140px] h-8 text-[11px] bg-transparent focus:ring-0 ${textColorClass} ${borderThem}`}
            >
              <SelectValue placeholder="Sub Division" />
            </SelectTrigger>
            <SelectContent className={`${bgColorClass} border ${borderThem}`}>
              <SelectItem value="All">
                <span className={textColorClass}>All Sub-Divs</span>
              </SelectItem>
              <SelectItem value="Sub-Div 01">
                <span className={textColorClass}>Sub-Div 01</span>
              </SelectItem>
              <SelectItem value="Sub-Div 02">
                <span className={textColorClass}>Sub-Div 02</span>
              </SelectItem>
              <SelectItem value="Sub-Div 03">
                <span className={textColorClass}>Sub-Div 03</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className={`h-[calc(100vh-57px)] pb-10 overflow-y-auto ${scrollBg} px-6 md:px-10 pt-3`}
      >
        <div className="px-3">
          <AssetReportMap
            statusColors={statusColors}
            DUMMY_ASSETS={DUMMY_ASSETS}
            mapHeight="calc(100vh - 100px)"
            statusFilter={statusFilter}
            divisionFilter={divisionFilter}
            subDivisionFilter={subDivisionFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default AssetReports;
